/**
 * Utilitários para organização mensal de entradas financeiras
 */

import { entryCountsInOperationalFinance } from '@/utils/financialOperationalImpact';

interface FinancialEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  payment_date?: string;
  status: string;
  created_at: string;
  client_id?: string;
  case_id?: string;
  clients?: {
    name: string;
    email: string;
  };
  cases?: {
    title: string;
    case_number?: string;
  };
  payment_confirmed?: boolean;
  is_negotiating?: boolean;
  is_installment?: boolean;
  installments_count?: number;
  installment_frequency?: string;
  down_payment?: number;
  is_conditional_payment?: boolean;
  conditional_type?: string;
  actual_payment_date?: string;
  is_overdue?: boolean;
  overdue_days?: number;
  rescheduled_count?: number;
  last_reschedule_date?: string;
  payment_notes?: string;
}

interface MonthlyGroup {
  month: string; // YYYY-MM
  monthLabel: string; // Agosto 2025
  entries: FinancialEntry[];
  summary: {
    totalRevenue: number;
    totalExpenses: number;
    netAmount: number;
    pendingRevenue: number;
    pendingExpenses: number;
    paidRevenue: number;
    paidExpenses: number;
  };
}

/**
 * Obtém o mês de uma entrada (baseado na data de pagamento ou criação).
 * Usa YYYY-MM-DD dos primeiros caracteres quando existir, para não mudar o mês por UTC.
 */
export const getEntryMonth = (entry: FinancialEntry): string => {
  const dateStr = entry.payment_date || entry.created_at;
  if (!dateStr) return '';

  const head = String(dateStr).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
    return head.slice(0, 7);
  }

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Formata o label do mês
 */
export const formatMonthLabel = (monthKey: string): string => {
  const [year, month] = monthKey.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
};

/** Ordena para a lista: pendentes primeiro; depois data de pagamento (mais próxima primeiro). */
export const sortEntriesByPaymentProximity = (
  a: FinancialEntry,
  b: FinancialEntry
): number => {
  if (a.status === "Pendente" && b.status !== "Pendente") return -1;
  if (a.status !== "Pendente" && b.status === "Pendente") return 1;

  const rawA = String(a.payment_date || a.created_at || "");
  const rawB = String(b.payment_date || b.created_at || "");
  const headA = rawA.slice(0, 10);
  const headB = rawB.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(headA) && /^\d{4}-\d{2}-\d{2}$/.test(headB)) {
    return headA.localeCompare(headB);
  }
  const dateA = new Date(a.payment_date || a.created_at);
  const dateB = new Date(b.payment_date || b.created_at);
  return dateA.getTime() - dateB.getTime();
};

/**
 * Agrupa entradas por mês
 */
export const groupByMonth = (entries: FinancialEntry[]): MonthlyGroup[] => {
  const monthGroups = new Map<string, FinancialEntry[]>();
  
  entries.forEach(entry => {
    const month = getEntryMonth(entry);
    if (!month) return;
    
    if (!monthGroups.has(month)) {
      monthGroups.set(month, []);
    }
    monthGroups.get(month)!.push(entry);
  });
  
  return Array.from(monthGroups.entries())
    .map(([month, entries]) => {
      const revenues = entries.filter(e => e.entry_type === 'receita');
      const expenses = entries.filter(e => e.entry_type === 'despesa');

      const revenuesOp = revenues.filter(entryCountsInOperationalFinance);
      const expensesOp = expenses.filter(entryCountsInOperationalFinance);
      
      const pendingRevenues = revenuesOp.filter(e => e.status === 'Pendente');
      const pendingExpenses = expensesOp.filter(e => e.status === 'Pendente');
      const paidRevenues = revenuesOp.filter(e => e.status === 'Pago');
      const paidExpenses = expensesOp.filter(e => e.status === 'Pago');
      
      const totalRevenue = revenuesOp.reduce((sum, e) => sum + e.amount, 0);
      const totalExpenses = expensesOp.reduce((sum, e) => sum + e.amount, 0);
      const pendingRevenue = pendingRevenues.reduce((sum, e) => sum + e.amount, 0);
      const pendingExpensesAmount = pendingExpenses.reduce((sum, e) => sum + e.amount, 0);
      const paidRevenue = paidRevenues.reduce((sum, e) => sum + e.amount, 0);
      const paidExpensesAmount = paidExpenses.reduce((sum, e) => sum + e.amount, 0);
      
      return {
        month,
        monthLabel: formatMonthLabel(month),
        entries: [...entries].sort(sortEntriesByPaymentProximity),
        summary: {
          totalRevenue,
          totalExpenses,
          // Saldo: só o que já foi recebido e pago; pendente é previsão
          netAmount: paidRevenue - paidExpensesAmount,
          pendingRevenue,
          pendingExpenses: pendingExpensesAmount,
          paidRevenue,
          paidExpenses: paidExpensesAmount
        }
      };
    })
    .sort((a, b) => a.month.localeCompare(b.month)); // Mais antigo primeiro
}; 